import { LatLon } from '../../util/geodesy/latlon';
import { parseLatLon } from '../../airport/util/parseUtils';


/**
 * Airport autocomplete search result model
 */
export class AirportAutocompleteResult {
  /**
   * Numeric identifier that uniquely identifies this airport in the database
   */
  airportId: number;

  /**
   * 4-letter ICAO identifier of the airport (if available)
   */
  icaoId: string;

  /**
   * 3- or 4-letter FAA identifier of the airport (if available)
   */
  faaId: string;

  /**
   * Gets the identifier that should be displayed for this airport. This will either be the ICAO identifier if
   * available, or the FAA identifier otherwise.
   */
  get identifier(): string {
    return this.icaoId || this.faaId || '';
  }

  /**
   * Airport name
   */
  name: string;

  /**
   * Airport latitude and longitude
   */
  location: LatLon;

  /**
   * Indicates whether this airport is public use (null if no data).
   */
  publicUse: boolean;

  /**
   * Associated city being served by this airport (note: not necessarily the same city where the airport is physically
   * located).
   */
  city: string;

  /**
   * State or province abbreviation of city being served by this airport (note: not necessarily the same state where the
   * airport is physically located).
   */
  stateProvince: string;

  /**
   * Associated country.
   */
  country: string;

  /**
   * SourceId
   */
  sourceId: number;

  /**
   * Parses an airport as returned by the API into an instance of the Airport class
   */
  static parse(data: any): AirportAutocompleteResult {
    return Object.assign(new AirportAutocompleteResult(), {
      airportId: data.airportId,
      icaoId: data.icaoId,
      faaId: data.faaId,
      name: data.name,
      location: data.location ? parseLatLon(data.location.latitude, data.location.longitude) : null,
      publicUse: this.parsePublicUse(data.airportUsage),
      city: data.city || null,
      stateProvince: data.stateProvince || null,
      country: data.country || null,
      sourceId: data.sourceId
    });
  }

  /**
   * Parses airport usage type from string
   * @param type {string} Airport usage type as returned by API
   * @return {boolean} Parsed airport usage type
   */
  static parsePublicUse(type: string): boolean {
    switch (type) {
      case 'public':
        return true;
      case 'private':
        return false;
      default:
        return null;
    }
  }
}
